import { Link } from 'react-router-dom';
import { siteConfig, categories, features, stats } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Home() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteConfig.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20abstract%20background%20with%20flowing%20organic%20shapes%20in%20soft%20coral%20amber%20and%20ivory%20tones%2C%20creative%20energy%20visualized%20through%20gentle%20curves%20and%20gradients%2C%20minimalist%20modern%20aesthetic%2C%20clean%20and%20inspiring%20atmosphere%2C%20no%20text%2C%20editorial%20style%20with%20soft%20ambient%20lighting&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
            alt="17一起草 - 内容创作平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
              {siteConfig.tagline}
            </h1>
            <p className="text-base md:text-lg text-white/85 mb-8 max-w-xl mx-auto leading-relaxed">
              {siteConfig.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/xiezuo-jiqiao"
                className="px-8 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即开始创作
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 rounded-md border border-white/40 text-white font-medium text-sm hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">为什么选择17一起草</h2>
              <p className="text-foreground-600 max-w-lg mx-auto leading-relaxed">
                六大核心能力，全方位助力你的创作之旅
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100/70 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">六大内容分类</h2>
              <p className="text-foreground-600 max-w-lg mx-auto leading-relaxed">
                无论你需要哪种类型的写作指导，这里都能找到
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-accent-200 transition-all duration-300 group cursor-pointer"
                >
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100/80 text-accent-500 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950">{cat.name}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">精选文章</h2>
                <p className="text-sm text-foreground-600">优质内容，助你提升写作水平</p>
              </div>
              <Link
                to="/category/xiezuo-jiqiao"
                className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Article 1 */}
              <Link
                to="/article/ruhe-kaishi-xiezuo"
                className="rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden group cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20study%20desk%20with%20notebook%20and%20pen%20bathed%20in%20soft%20morning%20sunlight%2C%20potted%20plant%20nearby%2C%20clean%20aesthetic%2C%20peaceful%20creative%20atmosphere%2C%20editorial%20photography%20style%20with%20warm%20amber%20tones%20and%20gentle%20shadows&width=600&height=400&seq=article-01&orientation=landscape"
                    alt="如何克服写作恐惧：从零开始的第一步"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100/80 text-accent-600 font-medium">写作技巧</span>
                    <span className="text-xs text-foreground-500">2026-07-28</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    如何克服写作恐惧：从零开始的第一步
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                    每个人都曾面对空白文档感到茫然。本文分享五个实用方法，帮助你克服写作恐惧，迈出创作的第一步。
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line"></i> 8 分钟阅读
                    </span>
                    <span className="text-sm text-primary-500 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      阅读更多 <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>

              {/* Article 2 */}
              <Link
                to="/article/chuangyi-xiezuo-siwei"
                className="rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden group cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Colorful%20abstract%20paint%20splashes%20swirling%20in%20creative%20motion%20on%20white%20canvas%2C%20artistic%20explosion%20of%20warm%20coral%20and%20teal%20colors%2C%20inspiring%20creative%20energy%2C%20high%20detail%20editorial%20photography%20with%20soft%20lighting&width=600&height=400&seq=article-02&orientation=landscape"
                    alt="打破思维定式：五种创意写作训练法"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100/80 text-accent-600 font-medium">创意灵感</span>
                    <span className="text-xs text-foreground-500">2026-07-25</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    打破思维定式：五种创意写作训练法
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                    创意不是天赋，而是可以训练的能力。掌握这五种创意写作训练方法，让你的文字从平淡变得灵动。
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line"></i> 10 分钟阅读
                    </span>
                    <span className="text-sm text-primary-500 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      阅读更多 <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>

              {/* Article 3 */}
              <Link
                to="/article/neirong-yingxiao-xinfa"
                className="rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden group cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20marketing%20strategy%20visualized%20through%20floating%20holographic%20data%20charts%20and%20content%20icons%20in%20warm%20amber%20and%20teal%2C%20clean%20minimalist%20aesthetic%2C%20futuristic%20yet%20warm%20atmosphere%2C%20editorial%20style%20with%20soft%20ambient%20light&width=600&height=400&seq=article-03&orientation=landscape"
                    alt="2026内容营销新趋势：从流量思维到价值思维"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100/80 text-accent-600 font-medium">内容营销</span>
                    <span className="text-xs text-foreground-500">2026-07-22</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    2026内容营销新趋势：从流量思维到价值思维
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                    内容营销正在经历深刻变革。理解从流量思维到价值思维的转变，是每个内容创作者必须掌握的底层逻辑。
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line"></i> 12 分钟阅读
                    </span>
                    <span className="text-sm text-primary-500 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      阅读更多 <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20gradient%20waves%20in%20warm%20coral%20and%20soft%20cream%20tones%2C%20dreamy%20ethereal%20atmosphere%2C%20minimalist%20modern%20design%2C%20inspiring%20creative%20energy%2C%20clean%20smooth%20curves%2C%20artistic%20background%20with%20gentle%20light%20effect&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          <div className="relative z-10 px-4 md:px-6 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">准备好开始你的创作之旅了吗？</h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              加入50万+创作者的行列，让每一次写作都更轻松、更高效、更有价值
            </p>
            <Link
              to="/category/xiezuo-jiqiao"
              className="inline-block px-8 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              免费开始使用
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}